import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";

interface CustomerService {
  id: number;
  name: string;
  wechatQrUrl: string;
  wechatNumber: string;
  autoReply: string;
}

export default function Contact() {
  const navigate = useNavigate();
  const [csList, setCsList] = useState<CustomerService[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<{ list: CustomerService[] }>("/public/contact")
      .then((res) => setCsList(res.list || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="flex-1 px-4 py-4">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={() => navigate(-1)}
          className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center cursor-pointer shrink-0">
          <i className="ri-arrow-left-s-line text-foreground-600 text-lg" />
        </button>
        <h1 className="text-base font-bold text-foreground-950">联系客服</h1>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : csList.length === 0 ? (
        <div className="bg-white rounded-xl p-8 border border-background-200/60 text-center">
          <i className="ri-customer-service-2-line text-4xl text-foreground-300" />
          <p className="text-sm text-foreground-500 mt-3">暂无在线客服</p>
          <p className="text-xs text-foreground-400 mt-1">请在工作时间联系，或发送邮件至 support@58888ai.com</p>
        </div>
      ) : (
        <div className="space-y-4">
          {csList.map((cs) => (
            <div key={cs.id} className="bg-white rounded-xl border border-background-200/60 overflow-hidden">
              {/* Header */}
              <div className="px-4 pt-4 pb-3 flex items-center gap-3 border-b border-background-100">
                <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-customer-service-2-fill text-primary-500 text-lg" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-foreground-950">{cs.name}</h2>
                  <p className="text-[10px] text-foreground-400">在线客服</p>
                </div>
              </div>

              {/* QR Code */}
              {cs.wechatQrUrl && (
                <div className="px-4 py-4 flex flex-col items-center">
                  <div className="w-[180px] h-[180px] rounded-xl border border-background-200 overflow-hidden bg-white p-2">
                    <img src={cs.wechatQrUrl} alt="微信二维码"
                      className="w-full h-full object-contain"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                  </div>
                  <p className="text-xs text-foreground-400 mt-2">扫码添加微信客服</p>
                </div>
              )}

              {/* WeChat number */}
              {cs.wechatNumber && (
                <div className="px-4 pb-3">
                  <div className="bg-background-50 rounded-lg px-3 py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <i className="ri-wechat-line text-[#07C160] text-base" />
                      <span className="text-xs text-foreground-600">微信号</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground-950">{cs.wechatNumber}</span>
                      <button onClick={() => {
                        navigator.clipboard.writeText(cs.wechatNumber).catch(() => {});
                      }}
                        className="text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-600 cursor-pointer whitespace-nowrap">
                        复制
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Auto reply message */}
              {cs.autoReply && (
                <div className="px-4 pb-4">
                  <div className="bg-accent-50 rounded-lg px-3 py-2.5 flex items-start gap-2">
                    <i className="ri-information-line text-accent-500 text-sm mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-foreground-600 leading-relaxed">{cs.autoReply}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Fallback note */}
      {csList.length > 0 && (
        <p className="text-[10px] text-foreground-400 text-center mt-4">
          工作时段 9:00-18:00（工作日）· 非工作时段留言会尽快回复
        </p>
      )}
    </div>
  );
}
